/**
 * `connectionHealthApi` — standalone RTK Query slice for the Connection Health
 * surface (Design §9). Backed by the proxy-server `connection_health_*`
 * Postgres tables (RLS-scoped by organization) and routed through the
 * `axilon-api-service` BFF under `/api/ontology/connection-health/*`
 * (`connection-health-routes.ts`), so the browser never talks to the proxy
 * directly — same approach as `dashboardsApi` / `ingestApi`.
 *
 * The Settings → Connection Health tab drives the status/rollup read views,
 * the org default-cadence control, and an idempotent check backfill for orgs
 * ingested before AXI-4537. A cadence change takes effect fleet-wide on the
 * next runner tick (no re-registration), so `updateSettings` only needs to
 * invalidate the settings cache. Backfill invalidates status/rollup.
 */

import { createApi } from '@reduxjs/toolkit/query/react'

import { getOntologyServiceBaseUrl } from '../../config'
import { ontologyBaseQuery } from '../core'
import type {
  BackfillConnectionHealthChecksResult,
  ConnectionHealthRollupResponse,
  ConnectionHealthSettings,
  ConnectionHealthStatusResponse,
  UpdateConnectionHealthSettingsPayload
} from './types'

const ONTOLOGY_SERVICE_URL = getOntologyServiceBaseUrl()

export const CONNECTION_HEALTH_TAG_TYPES = [
  'ConnectionHealthSettings',
  'ConnectionHealthStatus'
] as const
export type ConnectionHealthTagType = (typeof CONNECTION_HEALTH_TAG_TYPES)[number]

export const connectionHealthApi = createApi({
  reducerPath: 'connectionHealthApi',
  baseQuery: ontologyBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: CONNECTION_HEALTH_TAG_TYPES,
  endpoints: (builder) => ({
    getConnectionHealthStatus: builder.query<
      ConnectionHealthStatusResponse,
      { name?: string } | void
    >({
      query: (args) => {
        const name = args?.name?.trim()
        const suffix = name ? `?name=${encodeURIComponent(name)}` : ''
        return `${ONTOLOGY_SERVICE_URL}/connection-health/status${suffix}`
      },
      providesTags: ['ConnectionHealthStatus']
    }),
    getConnectionHealthRollup: builder.query<ConnectionHealthRollupResponse, void>({
      query: () => `${ONTOLOGY_SERVICE_URL}/connection-health/rollup`,
      providesTags: ['ConnectionHealthStatus']
    }),
    getConnectionHealthSettings: builder.query<ConnectionHealthSettings, void>({
      query: () => `${ONTOLOGY_SERVICE_URL}/connection-health/settings`,
      providesTags: [{ type: 'ConnectionHealthSettings', id: 'CURRENT' }]
    }),
    updateConnectionHealthSettings: builder.mutation<
      ConnectionHealthSettings,
      UpdateConnectionHealthSettingsPayload
    >({
      query: (body) => ({
        url: `${ONTOLOGY_SERVICE_URL}/connection-health/settings`,
        method: 'PUT',
        body
      }),
      invalidatesTags: [{ type: 'ConnectionHealthSettings', id: 'CURRENT' }]
    }),
    backfillConnectionHealthChecks: builder.mutation<BackfillConnectionHealthChecksResult, void>({
      query: () => ({
        url: `${ONTOLOGY_SERVICE_URL}/connection-health/checks:backfill`,
        method: 'POST'
      }),
      invalidatesTags: ['ConnectionHealthStatus']
    })
  })
})

export const {
  useGetConnectionHealthStatusQuery,
  useGetConnectionHealthRollupQuery,
  useGetConnectionHealthSettingsQuery,
  useUpdateConnectionHealthSettingsMutation,
  useBackfillConnectionHealthChecksMutation
} = connectionHealthApi
